/**
 * Server-Sent Events for the live queue (ADR-0004). The staff stream is one-directional push:
 * positions/ETAs are recomputed server-side on every mutation and emitted as `queue` events, plus
 * `ping` heartbeats every 25s. EventSource auto-reconnects; in a prod (cross-origin) build it needs
 * `withCredentials` to carry the session cookie. Staff actions stay ordinary authenticated POSTs.
 */
import { useEffect, useRef, useState } from 'react';
import { env } from './env.js';

export type SseStatus = 'connecting' | 'open' | 'closed';

export interface SseHandlers {
  [eventName: string]: (data: unknown) => void;
}

/**
 * Subscribe to an SSE endpoint. `path` is API-relative (e.g. '/v1/queue/stream'). Handlers are
 * keyed by event name. Returns the live connection status. The effect re-runs when `enabled` flips,
 * so the stream is torn down on logout and reopened on login.
 */
export function useEventSource(path: string, handlers: SseHandlers, enabled = true): SseStatus {
  const [status, setStatus] = useState<SseStatus>('closed');
  // Keep the latest handlers without re-subscribing on every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) {
      setStatus('closed');
      return;
    }
    setStatus('connecting');
    const url = `${env.apiBaseUrl}${path}`;
    const es = new EventSource(url, { withCredentials: env.crossOrigin });

    es.onopen = () => setStatus('open');
    es.onerror = () => setStatus('connecting'); // browser retries automatically

    const listeners: Array<[string, EventListener]> = [];
    for (const name of Object.keys(handlersRef.current)) {
      const listener: EventListener = (ev) => {
        const me = ev as MessageEvent;
        let data: unknown = me.data;
        try {
          data = JSON.parse(me.data as string);
        } catch {
          /* non-JSON (e.g. ping payloads) — pass through raw */
        }
        handlersRef.current[name]?.(data);
      };
      es.addEventListener(name, listener);
      listeners.push([name, listener]);
    }

    return () => {
      for (const [name, listener] of listeners) es.removeEventListener(name, listener);
      es.close();
      setStatus('closed');
    };
  }, [path, enabled]);

  return status;
}
